import { useLocation } from 'react-router-dom'
import { useRef, useEffect, useCallback } from 'react'
import { HandbookItemType } from 'types'
import { DuplicateWarningModal } from 'components/DuplicateWarningModal/DuplicateWarningModal'
import { ConfirmPublishingDialog } from 'components/Handbook2_0/AddDocsToHandbook/ConfirmPublishingDialog'
import { useModalStore } from 'hooks/stores/useModalStore'
import { useHandbookItems } from '../HandbookItemProvider'

// Fixes a react router issue:
// Given a focused input, when navigating to another URL via react-router the input doesn't receive the "blur" event.
// @see https://github.com/remix-run/react-router/issues/2973
export function useBlurOnNavigateRef<T extends { blur: () => void }>() {
  const ref = useRef<T>(null)
  const firstRender = useRef(true)
  const location = useLocation()

  useEffect(() => {
    !firstRender.current && ref.current?.blur()
  }, [location])
  useEffect(() => {
    firstRender.current = false
  }, [])

  return ref
}

export function useOnSelectAlgoliaItem(position: number) {
  const { createItem, currentHandbook: handbook, treeStore } = useHandbookItems()
  const { openModal, closeModal } = useModalStore()

  return useCallback(
    (algoliaItem: any) => {
      if (!treeStore?.tree) return

      const type = HandbookItemType.Document
      const isAlreadyInHandbook = algoliaItem.key && treeStore.isFileInTree(algoliaItem.key, type)
      const is_mention = !!handbook?.folder
      const handbookPublished = !!handbook?.shared_to_web || !!handbook?.published_at

      if (isAlreadyInHandbook) {
        openModal(DuplicateWarningModal, {
          customModal: true,
          modalProps: {
            title: algoliaItem.title,
            onClose: closeModal,
            folderName: handbook?.folder?.title || 'this folder',
          },
        })
      } else if (handbookPublished) {
        openModal(ConfirmPublishingDialog, {
          customModal: true,
          modalProps: {
            doc: algoliaItem.title,
            onClose: closeModal,
            onConfirm: () => {
              createItem({ type, position, key: algoliaItem.key, is_mention })
              closeModal()
            },
          },
        })
      } else {
        createItem({ type, position, key: algoliaItem.key, is_mention })
      }
    },
    [createItem, treeStore, closeModal, handbook, openModal, position],
  )
}
